import React, { useState, useContext, useEffect, useRef } from 'react';
import { useSearch } from '../hooks';
import { isClient } from '../../../utils/is-client';
import { debounce } from '../../../utils/debounce';

const SearchStateContext = React.createContext();

const SearchProvider = ({ children }) => {
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [pagesTotal, setPagesTotal] = useState(0);
  const [searchResults, searchResultsTotal, source] = useSearch(query);
  const [searchResultsPaginated, setSearchResultsPaginated] = useState([]);
  const [searchResultsLoading, setSearchResultsLoading] = useState(false);
  const [shouldUpdateQueryParam, setShouldUpdateQueryParam] = useState(true);
  const params = isClient() ? new URLSearchParams(window.location.search) : {};

  useEffect(() => {
    if (!isClient()) return;
    const query = params.get('query');

    if (query) setQuery(query);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setSearchResultsLoading(true);
    const getPagesTotal = () => {
      return Math.ceil(searchResultsTotal / limit);
    };

    const getResultsPaginated = () => {
      const start = (page - 1) * limit;
      const end = page * limit;

      return searchResults.slice(start, end);
    };

    setPagesTotal(getPagesTotal());

    setSearchResultsPaginated(getResultsPaginated());
    setSearchResultsLoading(false);
  }, [searchResults, searchResultsTotal, limit, page]);

  const updateQueryParam = useRef(
    debounce((type, value, updateqp) => {
      if (!updateqp || !isClient()) return;

      switch (type) {
        case 'set':
          params.set('query', value);
          break;
        case 'delete':
          params.delete('query');
          break;
        default:
          break;
      }

      window.history.pushState(null, null, `?${params.toString()}`);
    }, 250)
  );

  const handleSearchChange = (value) => {
    if (value.length === 0) handleSearchClear();

    setQuery(value);
    updateQueryParam.current('set', value, shouldUpdateQueryParam);
  };

  const handleSearchClear = () => {
    setQuery('');
    updateQueryParam.current('delete', shouldUpdateQueryParam);
  };

  const handlePageChange = (number) => {
    setPage(number);
  };

  return (
    <SearchStateContext.Provider
      value={{
        query,
        results: searchResultsPaginated,
        resultsTotal: searchResultsTotal,
        resultsLoading: searchResultsLoading,
        source,
        page,
        pagesTotal,
        onSearchChange: handleSearchChange,
        onSearchClear: handleSearchClear,
        onPageChange: handlePageChange,
        shouldUpdateQueryParam: (x) => setShouldUpdateQueryParam(x)
      }}
    >
      {children}
    </SearchStateContext.Provider>
  );
};

const useSearchState = () => {
  const context = useContext(SearchStateContext);
  if (context === undefined) {
    throw new Error('useSearchState must be used within a SearchProvider');
  }
  return context;
};

export { SearchProvider, useSearchState };
